import {useEffect,useMemo,useRef,useState} from 'react';
import {ExpressCheckoutElement as IntentWalletElement} from '@stripe/react-stripe-js';
import {ExpressCheckoutElement as CheckoutWalletElement} from '@stripe/react-stripe-js/checkout';
import {loadStripe} from '@stripe/stripe-js/pure';
import type {Appearance,StripeCheckoutExpressCheckoutElementOptions,StripeExpressCheckoutElementConfirmEvent,Stripe,StripeCheckoutContact,StripeCheckoutSession} from '@stripe/stripe-js';
import type {ReadyCheckout} from './checkout-business-details';
import {ErrorNotice,Loading} from './components';
import {errorDetails} from './lib/store';
import type {ErrorDetails} from './lib/types';
import './checkout-wallets.css';

export type WalletConfirmation=StripeExpressCheckoutElementConfirmEvent;
export const checkoutAppearance={variables:{spacingGridRow:'16px',spacingGridColumn:'16px'}} satisfies Appearance;
const walletOptions={
  buttonHeight:48,
  buttonTheme:{applePay:'black',googlePay:'black'},
  buttonType:{applePay:'buy',googlePay:'buy'},
  layout:{maxColumns:2,overflow:'never'},
  paymentMethodOrder:['apple_pay','google_pay'],
  paymentMethods:{applePay:'always',googlePay:'always',link:'never',paypal:'never',amazonPay:'never',klarna:'never'},
} satisfies StripeCheckoutExpressCheckoutElementOptions;

export function CheckoutWallets({checkout,publishableKey,purpose='purchase',busy,onConfirm}:{checkout?:ReadyCheckout;publishableKey?:string;purpose?:'purchase'|'subscription'|'setup';busy:boolean;onConfirm:(event:WalletConfirmation,collected?:CollectedWallet)=>void}){
  const options=useMemo(()=>({...walletOptions,...(checkout?{paymentMethods:{...walletOptions.paymentMethods,googlePay:'never' as const}}:{}),buttonType:purpose==='subscription'?{applePay:'subscribe' as const,googlePay:'subscribe' as const}:purpose==='setup'?{applePay:'plain' as const,googlePay:'plain' as const}:walletOptions.buttonType}),[purpose,Boolean(checkout)]);
  const [available,setAvailable]=useState<{applePay:boolean;googlePay:boolean}|null>(null),[error,setError]=useState<ErrorDetails|null>(null);
  const [googleAvailable,setGoogleAvailable]=useState<boolean|null>(null);
  const stripe=useMemo(()=>publishableKey?loadStripe(publishableKey):null,[publishableKey]);
  const empty=available!==null&&!available.applePay&&!(checkout?googleAvailable:available.googlePay);
  const Element=checkout?CheckoutWalletElement:IntentWalletElement;
  return <section className="checkout-wallets" aria-label="Apple Pay and Google Pay">
    <h2>{purpose==='setup'?'Use a digital wallet':'Pay with a digital wallet'}</h2>
    <Loading active={(available===null||Boolean(checkout)&&googleAvailable===null)&&!error}/>
    <div className={`checkout-wallet-buttons${checkout?' checkout-wallet-pair':''}`} data-empty={empty} inert={busy}>
      <div hidden={available!==null&&(checkout?!available.applePay:empty)}>
      <Element options={options} onConfirm={event=>{
        if(busy){event.paymentFailed({reason:'fail',message:'A payment is already being processed.'});return;}
        onConfirm(event);
      }} onReady={event=>setAvailable(current=>current??{applePay:Boolean(event.availablePaymentMethods?.applePay),googlePay:Boolean(event.availablePaymentMethods?.googlePay)})}
      onAvailablePaymentMethodsChange={event=>setAvailable({applePay:Boolean(event.paymentMethods?.applePay?.available),googlePay:Boolean(event.paymentMethods?.googlePay?.available)})}
      onLoadError={event=>setError(errorDetails(event.error))}/>
      </div>
      {checkout&&stripe&&<div hidden={googleAvailable===false}>
        <CheckoutGooglePay stripe={stripe} amount={checkout.total.total.minorUnitsAmount} currency={checkout.currency} purpose={purpose} busy={busy} onAvailable={setGoogleAvailable} onError={setError} onConfirm={onConfirm}/>
      </div>}
    </div>
    {available&&!error&&!available.applePay&&<p className="billing-note">
      {empty?'No express payment buttons are available here. Use the payment options below.'
        :'Apple Pay is not available here. Use another payment option below.'}
    </p>}
    <ErrorNotice error={error}/>
  </section>;
}

// This carries only the wallet approval for this attempt. Checkout owns prices and tax.
export type CollectedWallet={amount:number;currency:string;collect:()=>Promise<string>};

function CheckoutGooglePay(props:{stripe:Promise<Stripe|null>;amount:number;currency:string;purpose:'purchase'|'subscription'|'setup';busy:boolean;onAvailable:(available:boolean)=>void;onError:(error:ErrorDetails|null)=>void;onConfirm:(event:WalletConfirmation,collected:CollectedWallet)=>void}){
  const host=useRef<HTMLDivElement>(null),current=useRef(props),update=useRef<(()=>void)|null>(null);
  current.current=props;
  useEffect(()=>{
    let stopped=false,dispose:(()=>void)|undefined;
    void props.stripe.then(stripe=>{
      if(stopped)return;
      if(!stripe||!host.current)throw new Error('Google Pay could not load the payment service.');
      let appliedAmount=current.current.amount,pendingAmount:number|null=null,approved:{amount:number;currency:string}|null=null;
      // A separate Elements instance collects Google Pay; the same Checkout Session confirms it.
      const elements=stripe.elements({mode:props.purpose==='subscription'?'subscription':'payment',currency:props.currency,amount:appliedAmount,
        paymentMethodCreation:'manual',allowedPaymentMethodTypes:['card'],...(props.purpose==='subscription'?{setupFutureUsage:'off_session' as const}:{}),appearance:checkoutAppearance});
      const element=elements.create('expressCheckout',{...walletOptions,
        buttonType:{googlePay:props.purpose==='subscription'?'subscribe':'buy'},billingAddressRequired:true,emailRequired:true,
        paymentMethods:{...walletOptions.paymentMethods,applePay:'never'}});
      const syncAmount=()=>{
        if(stopped||pendingAmount!==null||approved||current.current.busy||current.current.amount===appliedAmount)return;
        pendingAmount=current.current.amount;elements.update({amount:pendingAmount});
      };
      update.current=syncAmount;
      elements.on('update-end',()=>{if(pendingAmount!==null){appliedAmount=pendingAmount;pendingAmount=null;}syncAmount();});
      element.on('click',event=>{
        if(current.current.busy||pendingAmount!==null||appliedAmount!==current.current.amount){event.reject();return;}
        current.current.onError(null);approved={amount:appliedAmount,currency:props.currency};event.resolve();
      });
      element.on('cancel',()=>{approved=null;syncAmount();});
      element.on('ready',event=>{if(!stopped)current.current.onAvailable(Boolean(event.availablePaymentMethods?.googlePay));});
      element.on('availablepaymentmethodschange',event=>{if(!stopped)current.current.onAvailable(Boolean(event.paymentMethods?.googlePay?.available));});
      element.on('loaderror',event=>{if(!stopped)current.current.onError(errorDetails(event.error));});
      element.on('confirm',event=>{
        if(current.current.busy||!approved){event.paymentFailed({reason:'fail'});return;}
        const acceptance=approved;approved=null;
        current.current.onConfirm(event,{...acceptance,collect:async()=>{
          const submitted=await elements.submit();
          if(submitted.error)throw new Error(submitted.error.message,{cause:submitted.error});
          const result=await stripe.createPaymentMethod({elements});
          if(result.error)throw new Error(result.error.message,{cause:result.error});
          if(result.paymentMethod.card?.wallet?.type!=='google_pay')throw new Error('The payment method did not come from Google Pay.');
          return result.paymentMethod.id;
        }});
      });
      element.mount(host.current);
      dispose=()=>{element.destroy();};
    }).catch(error=>{if(!stopped)current.current.onError(errorDetails(error));});
    return()=>{stopped=true;update.current=null;dispose?.();};
  },[props.stripe,props.currency,props.purpose]);
  useEffect(()=>{update.current?.();},[props.amount,props.busy]);
  return <div ref={host}/>;
}

/** The rendered total or wallet approval owns this attempt's amount; Checkout owns the updated tax. */
export async function prepareCheckoutConfirmation(checkout:ReadyCheckout,prepareBusiness:()=>Promise<StripeCheckoutSession>,event?:WalletConfirmation,collected?:CollectedWallet){
  const approved=collected??{amount:checkout.total.total.minorUnitsAmount,currency:checkout.currency};
  if(collected&&event?.expressPaymentType!=='google_pay')throw new Error('The collected payment method does not match the Google Pay approval.');
  let session=await prepareBusiness();
  let wallet:{billingAddress:StripeCheckoutContact;email:string;collect:(()=>Promise<string>)|null}|undefined;
  if(event){
    if(!event.billingDetails?.email||!event.billingDetails.address)throw new Error('The digital wallet did not return the required billing address and email.');
    const {name,address,email}=event.billingDetails;
    const billingAddress:StripeCheckoutContact={name,address:{...address,line2:address.line2??''}};
    const updated=await checkout.updateBillingAddress(billingAddress);
    if(updated.type==='error')throw new Error(updated.error.message,{cause:updated.error});
    const contact=await checkout.updateEmail(email);
    if(contact.type==='error')throw new Error(contact.error.message,{cause:contact.error});
    session=contact.session;
    wallet={billingAddress,email,collect:collected?.collect??null};
  }
  if(session.tax.status!=='ready')throw new Error('Tax could not be calculated. Check your billing address and tax details before paying.');
  if(session.currency!==approved.currency||session.total.total.minorUnitsAmount!==approved.amount){
    throw new Error(`Your billing details changed the total to ${session.total.total.amount}. Review the total and choose your payment method again. No payment was submitted.`);
  }
  if(wallet?.collect){
    const paymentMethod=await wallet.collect();
    return {paymentMethod,email:wallet.email,billingAddress:wallet.billingAddress};
  }
  return event&&wallet?{expressCheckoutConfirmEvent:event,email:wallet.email,billingAddress:wallet.billingAddress}:{};
}
